import ApiPricingVariables from "api/ApiPricingVariables"
import BodyText from "components/Typography/BodyText"
import { IDevModePricingVariables } from "pages/purchase-plans/hooks/usePurchasePlan"
import {
  filterRelevantCalculatorParams,
  mapPricingCalculatorDebouncedValues
} from "pages/purchase-plans/mappers/mapPricingVariables"
import { mapSimulatedDevModeBreakdown } from "pages/purchase-plans/mappers/mapSummary"
import { memo, useMemo, useState } from "react"
import { useMutation } from "react-query"
import styled from "styled-components"
import { useDebounce } from "use-debounce"
import useDeepCompareEffect from "use-deep-compare-effect"
import { uniqueKey } from "utils/uniqueKey"
import { validatedIfIsFilled } from "utils/validation/validateIfIsFilled"
import DefaultSummary from "./DefaultSummary"

const ContentSummaryItemContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ResultContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`

interface PlanSummaryProps {
  isDevMode: boolean
  fromPriceUnit?: string
  selectedProduct: string
  pricingCalculator?: IDevModePricingVariables
}

const DevModeSummary = (props: PlanSummaryProps) => {
  const { isDevMode, fromPriceUnit = "", selectedProduct = "", pricingCalculator } = props

  const [devModeBreakdown, setDevModeBreakdown] = useState<App.ISimulatedBreakdown>()

  const summaryContentToDevMode = useMemo(() => {
    if (!isDevMode || !devModeBreakdown) return

    return mapSimulatedDevModeBreakdown(devModeBreakdown)
  }, [devModeBreakdown, isDevMode, fromPriceUnit])

  const calculatePriceBreakdown = useMutation({
    mutationFn: async (infoSummary: { scope: App.PricingExpressionSummary[]; strExpression: string }) => {
      const data = await new ApiPricingVariables().calculateDevModeSummary({
        populatedPricingEntities: infoSummary.scope,
        strExpression: infoSummary.strExpression,
        productId: selectedProduct
      })

      return data
    },
    onSuccess: (data) => {
      setDevModeBreakdown(data.data)
    }
  })

  const debouncedPricingCalculator = useDebounce([pricingCalculator], 1000)

  useDeepCompareEffect(() => {
    if (!selectedProduct || !isDevMode || !pricingCalculator) return

    const { strExpression, scope } = mapPricingCalculatorDebouncedValues(pricingCalculator)

    if (!strExpression || strExpression?.length === 0 || !scope || scope?.length === 0) return

    const relevantParams = filterRelevantCalculatorParams(scope)

    const isScopeValid = relevantParams.every(validatedIfIsFilled)

    if (isScopeValid) {
      calculatePriceBreakdown.mutate({ strExpression, scope })
    }
  }, [debouncedPricingCalculator, isDevMode, selectedProduct])

  const isValid = !!fromPriceUnit && !!devModeBreakdown?.totalPrice && isDevMode

  return (
    <>
      {!isValid && (
        <ResultContainer>
          <BodyText color="gray-medium" weight="semi-bold">
            Para gerar o resumo, preencha os campos em definições para precificação.
          </BodyText>
        </ResultContainer>
      )}

      {isValid && (
        <DefaultSummary fromPriceUnit={fromPriceUnit} totalPrice={summaryContentToDevMode?.totalPrice}>
          <>
            {summaryContentToDevMode?.priceBreakdown?.map((pricingVariable) => (
              <ContentSummaryItemContainer key={uniqueKey()}>
                <>
                  <BodyText size="small" weight="light" color="gray-dark">
                    {pricingVariable.price} x {pricingVariable.count} {pricingVariable.label} / {pricingVariable.unit}
                  </BodyText>
                  <BodyText size="small" weight="bold" color="gray-dark">
                    {pricingVariable.totalPrice}
                  </BodyText>
                </>
              </ContentSummaryItemContainer>
            ))}
          </>
        </DefaultSummary>
      )}
    </>
  )
}

export default memo(DevModeSummary)
